// ログイン前にいた画面（連携アプリの同意画面や Unity 連携画面など）へ、ログイン／新規登録を
// 挟んでも戻れるようにするための復帰先（return-to）ユーティリティ。
// location.state はリロードやタブ跨ぎで失われるため、新規登録〜メール確認のように
// 複数ページをまたぐ経路では URL クエリパラメータ `from` で引き回す。

/**
 * 復帰先の値を検証する。オープンリダイレクト対策として、自サイト内の相対パスのみ許可する。
 * `/` で始まり、`//` や `/\` で始まらない（プロトコル相対 URL の弾き）ことに加え、
 * スキームを含む値（`javascript:` 等）や制御文字を含む値も弾く。
 * 条件を満たさなければ null を返す。
 */
export function sanitizeReturnTo(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  if (/[\x00-\x1f]/.test(value)) return null;
  // "/javascript:alert(1)" のような、先頭の "/" の後にスキームが続く値を弾く。
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  return value;
}

/**
 * 復帰先を読み取る。location.state.from を優先し、無ければクエリパラメータ `from` を見る。
 * どちらの値もサニタイズを通し、不正な値は無視する（両方不正・未指定なら null）。
 */
export function readReturnTo(locationState: unknown, searchParams: URLSearchParams): string | null {
  const stateFrom = (locationState as { from?: string } | null | undefined)?.from;
  return sanitizeReturnTo(stateFrom) ?? sanitizeReturnTo(searchParams.get('from'));
}

/**
 * 遷移先パスに復帰先クエリ `from` を付与する。from が null ならそのまま返す。
 * 遷移先に既存のクエリパラメータがあっても正しく連結する。
 */
export function withReturnTo(path: string, from: string | null): string {
  if (!from) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}from=${encodeURIComponent(from)}`;
}
